/** 键盘板载 profile 切换：统一事件名与 detail 结构（BoardList / boardConfig / deviceStore 同步） */

import { getMusicRhythmBroadcastTargets } from "../musicRhythm/globalRhythmCapture";

export const KEYBOARD_BOARD_PROFILE_CHANGED_EVENT =
  "keyboard-board-config-changed";

export type KeyboardBoardProfileChangeSource =
  | "user"
  | "device"
  | "sync"
  | "auto";

export interface KeyboardBoardProfileChangedDetail {
  key: number;
  name?: string;
  source?: KeyboardBoardProfileChangeSource;
  deviceKey?: string;
}

export function resolveEquipmentDeviceKey(
  equipment?: Record<string, unknown> | null
): string {
  if (!equipment || typeof equipment !== "object") return "";
  const name = String(
    equipment.productName ??
      equipment.displayname ??
      equipment.displayName ??
      equipment.deviceId ??
      ""
  ).trim();
  if (name) return name;
  const vid = equipment.vendorId;
  const pid = equipment.productId;
  if (vid != null && pid != null) return `${vid}:${pid}`;
  return "";
}

export function dispatchKeyboardBoardProfileChangedOnWindow(
  target: Window,
  detail: KeyboardBoardProfileChangedDetail
): void {
  if (!Number.isFinite(detail.key)) return;
  try {
    target.dispatchEvent(
      new CustomEvent(KEYBOARD_BOARD_PROFILE_CHANGED_EVENT, {
        detail: {
          ...detail,
          source: detail.source ?? "user",
        },
      })
    );
  } catch {
    /* ignore */
  }
}

/** qiankun：主应用与子应用 window 隔离，须向 rawWindow/top 等同步广播 */
export function getKeyboardBoardProfileBroadcastTargets(): Window[] {
  return getMusicRhythmBroadcastTargets();
}

/** 子应用 / 主应用：boardConfig、BoardList、关联应用自动切换等切换成功后调用 */
export function notifyKeyboardBoardProfileChanged(
  key: number,
  opts?: Omit<KeyboardBoardProfileChangedDetail, "key">
): void {
  if (typeof window === "undefined") return;
  const detail: KeyboardBoardProfileChangedDetail = { key, ...opts };
  for (const target of getKeyboardBoardProfileBroadcastTargets()) {
    dispatchKeyboardBoardProfileChangedOnWindow(target, detail);
  }
}

/** 在全部广播目标上监听（子应用板载页须用此 API，勿只绑 sandbox window） */
export function subscribeKeyboardBoardProfileChanged(
  listener: (detail: KeyboardBoardProfileChangedDetail) => void
): () => void {
  if (typeof window === "undefined") return () => undefined;

  const handler = (event: Event) => {
    const detail = (event as CustomEvent<KeyboardBoardProfileChangedDetail>)
      .detail;
    if (typeof detail?.key !== "number") return;
    listener(detail);
  };

  const targets = getKeyboardBoardProfileBroadcastTargets();
  for (const target of targets) {
    target.addEventListener(KEYBOARD_BOARD_PROFILE_CHANGED_EVENT, handler);
  }

  return () => {
    for (const target of targets) {
      target.removeEventListener(KEYBOARD_BOARD_PROFILE_CHANGED_EVENT, handler);
    }
  };
}

/** 事件 deviceKey 与当前会话设备一致时才应更新 UI 选中态 */
export function shouldApplyBoardProfileChangeToDevice(
  eventDeviceKey: string | undefined,
  currentDeviceKey: string
): boolean {
  if (!eventDeviceKey || !currentDeviceKey) return true;
  return eventDeviceKey === currentDeviceKey;
}
